import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PageLayout from '../../components/ui/PageLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import CurrentPlanCard from './components/CurrentPlanCard';
import PricingPlansGrid from './components/PricingPlansGrid';
import UsageTrackingCard from './components/UsageTrackingCard';
import BillingHistoryCard from './components/BillingHistoryCard';
import AccountSettingsCard from './components/AccountSettingsCard';
import FeatureAccessCard from './components/FeatureAccessCard';
import PlanUpgradeModal from './components/PlanUpgradeModal';
import {
  getSubscriptionPlans,
  getUserSubscription,
  getUserUsage,
  cancelSubscription as cancelUserSubscription,
  getFeatureAccess
} from '../../services/subscriptionService';

// Correct pricing: Basic=$163, Professional=$384, Premium=$520, Enterprise=$649
const FALLBACK_PLANS = [
  { id: 'free', name: 'Free', amount: 0, description: 'Perfect for getting started', billing_interval: 'monthly', features: { leads: 100, emails: 500 } },
  { id: 'basic', name: 'Basic', amount: 163, description: 'Great for small businesses', billing_interval: 'monthly', features: { leads: 1000, emails: 5000 } },
  { id: 'professional', name: 'Professional', amount: 384, description: 'Most popular for growing teams', billing_interval: 'monthly', features: { leads: 5000, emails: 25000 } },
  { id: 'premium', name: 'Premium', amount: 520, description: 'Enterprise-grade solution', billing_interval: 'monthly', features: { leads: 15000, emails: 60000 } },
  { id: 'enterprise', name: 'Enterprise', amount: 649, description: 'For large organizations', billing_interval: 'monthly', features: { leads: 25000, emails: 100000 } },
];

const CardSkeleton = React.memo(() => (
  <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
    <div className="animate-pulse">
      <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
      <div className="h-8 bg-muted rounded w-1/2 mb-4"></div>
      <div className="space-y-2">
        <div className="h-3 bg-muted rounded w-full"></div>
        <div className="h-3 bg-muted rounded w-2/3"></div>
      </div>
    </div>
  </div>
));

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading, isInitialized } = useAuth();
  const toast = useToast();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [featureAccess, setFeatureAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState(null);

  const fetchSubscriptionData = useCallback(async () => {
    if (!user?.id || authLoading) return;

    try {
      setLoading(true);
      setError(null);

      const [plans, subscription, usage, access] = await Promise.allSettled([
        getSubscriptionPlans(),
        getUserSubscription(user?.id),
        getUserUsage(user?.id),
        getFeatureAccess(user?.id)
      ]);

      let plansData = plans?.status === 'fulfilled' ? (plans?.value || []) : [];
      // Normalize plan amounts: if stored in cents (>1000), convert to dollars
      plansData = plansData?.map(p => ({
        ...p,
        amount: p?.amount > 1000 ? Math.round(p?.amount / 100) : (p?.amount || 0)
      }));
      // Use fallback if no plans returned
      if (plansData?.length === 0) plansData = FALLBACK_PLANS;

      const subscriptionResult = subscription?.status === 'fulfilled' ? subscription?.value : null;
      const usageResult = usage?.status === 'fulfilled' ? (usage?.value || []) : [];
      const accessResult = access?.status === 'fulfilled' ? access?.value : null;

      setAvailablePlans(plansData);
      setSubscriptionData(subscriptionResult);
      setUsageData(usageResult);
      setFeatureAccess(accessResult);

      if (subscriptionResult?.subscription_plans) {
        const dbPlan = subscriptionResult?.subscription_plans;
        const planAmount = dbPlan?.amount > 1000 ? Math.round(dbPlan?.amount / 100) : (dbPlan?.amount || 0);
        setCurrentPlan({
          id: dbPlan?.name?.toLowerCase() || 'free',
          name: dbPlan?.name || 'Free',
          tier: dbPlan?.name || 'Free',
          price: planAmount,
          billingCycle: dbPlan?.billing_interval || 'monthly',
          features: dbPlan?.features || {},
          status: subscriptionResult?.status || 'active',
          nextBilling: subscriptionResult?.current_period_end
        });
      } else {
        setCurrentPlan({
          id: 'free',
          name: 'Free',
          tier: 'Free',
          price: 0,
          billingCycle: 'monthly',
          features: {},
          status: 'active',
          nextBilling: null
        });
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError('Failed to load subscription information. Please try again.');
      setAvailablePlans(FALLBACK_PLANS);
    } finally {
      setLoading(false);
    }
  }, [user?.id, authLoading]);

  useEffect(() => {
    if (!isInitialized || authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    fetchSubscriptionData();
  }, [user, authLoading, isInitialized, fetchSubscriptionData, navigate]);

  const handleUpgradePlan = (plan) => {
    setSelectedPlanForUpgrade(plan);
    setIsUpgradeModalOpen(true);
  };

  const handleCancelSubscription = async () => {
    if (!subscriptionData?.id) {
      toast?.warning('No active subscription to cancel.', 'No Subscription');
      return;
    }
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) return;
    try {
      const result = await cancelUserSubscription(subscriptionData?.id);
      if (result?.success) {
        toast?.success('Subscription cancelled successfully.', 'Cancelled');
        await fetchSubscriptionData();
      } else {
        throw new Error(result?.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      toast?.error(err?.message || 'Failed to cancel subscription.', 'Error');
    }
  };

  if (authLoading || loading) {
    return (
      <PageLayout>
        <Helmet><title>Subscription Management - LeadGen Pro</title></Helmet>
        <div className="space-y-6">
          <div className="h-8 bg-muted rounded w-1/3 animate-pulse mb-8"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[1, 2, 3]?.map(i => <CardSkeleton key={i} />)}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error && availablePlans?.length === 0) {
    return (
      <PageLayout>
        <Helmet><title>Subscription Management - LeadGen Pro</title></Helmet>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Unable to Load Subscription</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button variant="default" onClick={fetchSubscriptionData} iconName="RefreshCw" iconPosition="left">Try Again</Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Helmet>
        <title>Subscription Management - LeadGen Pro</title>
        <meta name="description" content="Manage your LeadGen Pro subscription, billing, and plan features" />
      </Helmet>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Subscription Management</h1>
          <p className="text-muted-foreground">Manage your plan, billing, and feature access</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={fetchSubscriptionData}>Refresh</Button>
          <Button variant="default" iconName="ArrowUp" iconPosition="left" onClick={() => handleUpgradePlan(null)}>Upgrade Plan</Button>
        </div>
      </div>

      {/* Current Plan */}
      <div className="mb-8">
        <CurrentPlanCard
          plan={currentPlan}
          subscription={subscriptionData}
          onUpgrade={() => handleUpgradePlan(null)}
          onCancel={handleCancelSubscription}
        />
      </div>

      {/* Usage & Features Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <UsageTrackingCard usageData={usageData} plan={currentPlan} />
        <FeatureAccessCard featureAccess={featureAccess} plan={currentPlan} />
      </div>

      {/* Available Plans */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-foreground mb-4">Available Plans</h2>
        <PricingPlansGrid
          plans={availablePlans}
          currentPlan={currentPlan}
          onSelectPlan={handleUpgradePlan}
        />
      </div>

      {/* Billing History */}
      <div className="mb-8">
        <BillingHistoryCard userId={user?.id} />
      </div>

      {/* Account Settings */}
      <AccountSettingsCard
        user={user}
        userProfile={userProfile}
        subscription={subscriptionData}
        onCancelSubscription={handleCancelSubscription}
      />

      {/* Upgrade Modal */}
      <PlanUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => { setIsUpgradeModalOpen(false); setSelectedPlanForUpgrade(null); }}
        selectedPlan={selectedPlanForUpgrade}
        currentPlan={currentPlan}
        availablePlans={availablePlans}
        onSuccess={() => {
          fetchSubscriptionData();
          toast?.success('Plan upgraded successfully!', 'Upgraded ✓');
        }}
      />
    </PageLayout>
  );
};

export default SubscriptionManagement;